<template>
  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="快递查询对接（平台级）：切换快递100/快递鸟并填写密钥后保存即生效，无需重新发版。未保存过时沿用环境 yaml。密钥回显已脱敏，未改动可直接保存。"
    />
    <el-form v-loading="expressLoading" :model="expressForm" label-width="140px">
      <el-form-item label="当前来源">
        <el-tag v-if="!expressLoaded" type="info">加载中…</el-tag>
        <el-tag v-else :type="expressForm.source === 'db' ? 'success' : 'info'">
          {{ expressForm.source === 'db' ? '后台已保存（DB）' : '环境配置（yaml 兜底）' }}
        </el-tag>
      </el-form-item>
      <el-form-item label="对接厂商" required>
        <el-radio-group
          :key="'express-' + expressForm.client + '-' + expressForm.source"
          v-model="expressForm.client"
        >
          <el-radio value="kd_100">快递100</el-radio>
          <el-radio value="kd_niao">快递鸟</el-radio>
          <el-radio value="not_provide">未接入</el-radio>
        </el-radio-group>
      </el-form-item>
      <template v-if="expressForm.client === 'kd_100'">
        <el-form-item label="Customer" required>
          <el-input
            v-model="expressForm.kd100Customer"
            class="!w-420px"
            clearable
            placeholder="快递100 customer"
          />
        </el-form-item>
        <el-form-item label="Key" required>
          <el-input
            v-model="expressForm.kd100Key"
            class="!w-420px"
            clearable
            placeholder="快递100 key"
            show-password
          />
        </el-form-item>
      </template>
      <template v-else-if="expressForm.client === 'kd_niao'">
        <el-form-item label="用户 ID" required>
          <el-input
            v-model="expressForm.kdniaoBusinessId"
            class="!w-420px"
            clearable
            placeholder="快递鸟 businessId"
          />
        </el-form-item>
        <el-form-item label="API Key" required>
          <el-input
            v-model="expressForm.kdniaoApiKey"
            class="!w-420px"
            clearable
            placeholder="快递鸟 apiKey"
            show-password
          />
        </el-form-item>
        <el-form-item label="接口指令">
          <el-radio-group v-model="expressForm.kdniaoRequestType">
            <el-radio value="1002">1002 免费版</el-radio>
            <el-radio value="8001">8001 付费版</el-radio>
          </el-radio-group>
        </el-form-item>
      </template>
      <el-form-item>
        <el-button
          v-hasPermi="['trade:config:save']"
          :loading="expressLoading"
          type="primary"
          @click="submitExpressForm"
        >
          保存快递对接
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as ExpressClientConfigApi from '@/api/mall/trade/expressClientConfig'

defineOptions({ name: 'TradeExpressClientConfig' })

const message = useMessage()

const expressLoading = ref(false)
const expressLoaded = ref(false)
const expressForm = ref<ExpressClientConfigApi.ExpressClientConfigVO>({
  source: 'yaml',
  client: 'not_provide',
  kd100Customer: '',
  kd100Key: '',
  kdniaoBusinessId: '',
  kdniaoApiKey: '',
  kdniaoRequestType: '1002'
})

/** 兼容正常解包 / 偶发未解包的 { code, data } 包一层 */
const normalizeExpressConfig = (raw: any): ExpressClientConfigApi.ExpressClientConfigVO | null => {
  if (!raw || typeof raw !== 'object') return null
  const payload = raw.client != null || raw.source != null ? raw : raw.data
  if (!payload || typeof payload !== 'object') return null
  return {
    source: payload.source === 'db' ? 'db' : 'yaml',
    client: payload.client || 'not_provide',
    kd100Customer: payload.kd100Customer || '',
    kd100Key: payload.kd100Key || '',
    kdniaoBusinessId: payload.kdniaoBusinessId || '',
    kdniaoApiKey: payload.kdniaoApiKey || '',
    kdniaoRequestType: payload.kdniaoRequestType || '1002'
  }
}

const getExpressConfig = async () => {
  expressLoading.value = true
  try {
    const raw = await ExpressClientConfigApi.getExpressClientConfig()
    const normalized = normalizeExpressConfig(raw)
    if (normalized) {
      expressForm.value = normalized
    }
  } catch (e) {
    console.error('加载快递对接配置失败', e)
  } finally {
    expressLoaded.value = true
    expressLoading.value = false
  }
}

const submitExpressForm = async () => {
  if (expressLoading.value) return
  if (expressForm.value.client === 'kd_100') {
    if (!expressForm.value.kd100Customer || !expressForm.value.kd100Key) {
      message.warning('请填写快递100 的 Customer 与 Key')
      return
    }
  }
  if (expressForm.value.client === 'kd_niao') {
    if (!expressForm.value.kdniaoBusinessId || !expressForm.value.kdniaoApiKey) {
      message.warning('请填写快递鸟的用户 ID 与 API Key')
      return
    }
  }
  expressLoading.value = true
  try {
    const savePayload = { ...expressForm.value }
    await ExpressClientConfigApi.saveExpressClientConfig(savePayload)
    message.success('快递对接配置已保存，立即生效')
    await getExpressConfig()
    // 回读异常时至少保持刚保存的厂商，避免闪回「未接入」
    if (expressForm.value.client === 'not_provide' && savePayload.client !== 'not_provide') {
      expressForm.value.client = savePayload.client
      expressForm.value.source = 'db'
    }
  } finally {
    expressLoading.value = false
  }
}

onMounted(async () => {
  await getExpressConfig()
})
</script>
